import React from "react";
import { arrayOf, number, shape, string } from "prop-types";
import propsResolver from "@insightfulscience/shared-utils/fn/props-resolver";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import parseNumber from "@insightfulscience/shared-utils/parsers/parseNumber";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import routes, { toLoginUrl } from "@src/routes";
import SelectSeatsForm from "@src/SelectSeatsForm";
import { savePriceItem } from "@src/SelectSeatsForm/action";
import SiteLayout from "@src/SiteLayout";
import redirectIf from "@src/utils/redirectIf";
import { withErrorHandling } from "@src/AppError";
import eCommerce from "@src/services/ecommerce";
import * as GraphQlError from "@src/AppError/mapGraphQLErrors";
import { isAuthorized } from "@src/services/auth";
import { usePersistentStorage } from "@src/persistent-storage/hooks";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import resources from "../../locales/select-seats-page";

const SelectNumberOfSeatsPage = props => {
	const [t] = useLocale("select-seats-page");

	usePersistentStorage();

	return (
		<SiteLayout>
			<PageMeta title={t("PAGE_TITLE")}>
				<PageMeta.Robots content={t("META_ROBOTS_CONTENT")} />
			</PageMeta>
			<SimpleTemplate>
				<SelectSeatsForm {...props} />
			</SimpleTemplate>
		</SiteLayout>
	);
};

SelectNumberOfSeatsPage.propTypes = {
	priceList: arrayOf(
		shape({
			subscriptionSize: number.isRequired,
			price: number.isRequired,
			betterOption: number,
		}),
	),
	planCode: string,
	initialSeats: number,
};

const planCodeNotSpecified = ({ query }) => query.planCode == null;

SelectNumberOfSeatsPage.getInitialProps = propsResolver({
	priceList: context => eCommerce(context).getPrices(context.query.planCode),
	planCode: ({ query }) => query.planCode,
	initialSeats: ({ query }) => parseNumber(query.seats),
});

const checkPriceListLength = expected => ({ priceList }) => priceList?.length === expected;

const savePriceItemAndGoToBillingInfo = ({ planCode, priceList }, context) => {
	const { subscriptionSize, price } = priceList[0];
	savePriceItem(context)(planCode, subscriptionSize, price);
	return isAuthorized(context)
		? routes.billingInfo //
		: toLoginUrl({ returnUrl: routes.billingInfo });
};

export default pipe(
	SelectNumberOfSeatsPage,
	WithLocale({ resources }),
	redirectIf(planCodeNotSpecified, routes.external.howToBuy),
	redirectIf.afterPropsResolved([
		[checkPriceListLength(0), routes.external.howToBuy],
		[checkPriceListLength(1), savePriceItemAndGoToBillingInfo],
	]),
	redirectIf(planCodeNotSpecified, routes.external.howToBuy),
	redirectIf.onCatch(GraphQlError.isNotFound, routes.external.howToBuy),
	withErrorHandling,
);
